import { useEffect } from 'react';
import type { StorageServiceRoot } from 'src/apis/storage/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { NotFound } from 'src/utils/errors';
import { handleErrorWithNotification } from 'src/utils/handle-error';

type ExecuteProps = {
  storageService: string;
};

export const storageServiceRootCacheKey = 'storage-service-root';
const storageServiceApi = PRISMA_CONFIG.storageService;

export function useStorageServiceRoot() {
  const storageServiceRoot = useAxios(
    (axios, baseConfig, { storageService }: ExecuteProps) =>
      axios.request<StorageServiceRoot>({
        ...baseConfig,
        url: storageService,
      }),
    { neededOnPageLoad: true, cacheKey: storageServiceRootCacheKey }
  );

  useEffect(() => {
    // TODO: change this once storage service is fully deployed to all stages
    if (!storageServiceApi) throw new NotFound();
    storageServiceRoot.execute({ storageService: storageServiceApi });
    // oxlint-disable-next-line exhaustive-deps
  }, [storageServiceApi]);

  return storageServiceRoot;
}

export type StorageServiceRootRequest = ReturnType<
  typeof useStorageServiceRoot
>;

/**
 * Use this hook, if you need to make a request to Storage Service in a critical path
 * when you don't want the whole page to crash on an error (e.g. in the header component).
 */
export function useOptionalStorageServiceRoot() {
  const storageServiceRoot = useAxios(
    (axios, baseConfig, { storageService }: ExecuteProps) =>
      axios.request<StorageServiceRoot>({
        ...baseConfig,
        url: storageService,
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      cacheKey: storageServiceRootCacheKey,
      onError(error) {
        // report errors, if available on stage
        // TODO: remove server error check once storage service is fully deployed to all stages
        if (storageServiceApi && !isServerError(error, 401)) {
          handleErrorWithNotification({
            error,
            setReauthenticate: () => {},
          });
        }
        return error;
      },
    }
  );

  useEffect(() => {
    if (!storageServiceApi) return;
    storageServiceRoot.execute({ storageService: storageServiceApi });
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  if (!storageServiceApi) return;

  return storageServiceRoot;
}

export type OptionalStorageServiceRootRequest = ReturnType<
  typeof useOptionalStorageServiceRoot
>;
